import { createContext, ReactNode, useMemo } from 'react';

import { IApiValidationError } from '../../models/ApiValidationError';
import { FieldErrorMessage } from '../../models/FieldErrorMessage';
import { useFormErrors } from './FormErrorProvider.hooks';

type FormErrorInfoState = {
  errors: FieldErrorMessage[];
};

type FormErrorActionableState<T = any> = {
  addErrors: (apiErrors: IApiValidationError[], values: T) => void;
  addError: (apiError: IApiValidationError, value: T) => void;
  removeErrors: (propertiesNames: string[]) => void;
  validateErrors: (values: T) => FieldErrorMessage[];
  clearErrors: () => void;
};

export const FormErrorInfoContext = createContext<FormErrorInfoState>({
  errors: [],
});

export const FormErrorActionableContext =
  createContext<FormErrorActionableState>({
    addErrors: null,
    addError: null,
    removeErrors: null,
    validateErrors: null,
    clearErrors: null,
  });

export type FormErrorProviderProps = {
  children?: ReactNode;
  initErrorGroups?: string[];
};

const FormErrorProvider = ({
  children,
  initErrorGroups,
}: FormErrorProviderProps) => {
  const {
    addErrors,
    addError,
    removeErrors,
    validateErrors,
    errors,
    clearErrors,
  } = useFormErrors({ initErrorGroups });

  const actionableValue = {
    addErrors,
    addError,
    removeErrors,
    validateErrors,
    clearErrors,
  };

  const infoValue = useMemo(
    () => ({
      errors,
    }),
    [errors],
  );

  return (
    <FormErrorInfoContext.Provider value={infoValue}>
      <FormErrorActionableContext.Provider value={actionableValue}>
        {children}
      </FormErrorActionableContext.Provider>
    </FormErrorInfoContext.Provider>
  );
};

export default FormErrorProvider;
